<template>
  <Menubar :model="items" class="navbar">
    <template #start>
      <RouterLink to="/" class="navbar-brand">
        <i class="pi pi-robot" style="font-size: 1.5rem; margin-right: 0.5rem;" />
        <span>Nexo</span>
      </RouterLink>
    </template>
    <template #end>
      <div class="user-menu">
        <span class="user-name" v-if="authStore.user">
          <i class="pi pi-user" />
          {{ authStore.user.full_name || authStore.user.email }}
        </span>
        <!-- Modo oscuro (Issue #43) -->
        <Button
          :icon="themeStore.isDark ? 'pi pi-sun' : 'pi pi-moon'"
          :aria-label="themeStore.isDark ? 'Activar modo claro' : 'Activar modo oscuro'"
          :title="themeStore.isDark ? 'Modo claro' : 'Modo oscuro'"
          text
          size="small"
          @click="themeStore.toggle()"
        />
        <!-- Centro de notificaciones (Issues #45–46) -->
        <NotificationCenter />
        <Button
          label="Perfil"
          icon="pi pi-id-card"
          text
          size="small"
          @click="router.push('/profile')"
        />
        <Button
          label="Salir"
          icon="pi pi-sign-out"
          text
          size="small"
          severity="secondary"
          @click="authStore.logout()"
        />
      </div>
    </template>
  </Menubar>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../stores/auth';
import { useThemeStore } from '../../stores/theme';
import Menubar from 'primevue/menubar';
import Button from 'primevue/button';
import NotificationCenter from '../financial/NotificationCenter.vue';

const router = useRouter();
const authStore = useAuthStore();
const themeStore = useThemeStore();

// Función helper para crear items de menú con navegación
const nav = (label, icon, path, children = null) => {
  const item = {
    label,
    icon,
    command: () => router.push(path),
  };
  if (children) {
    delete item.command;
    item.items = children;
  }
  return item;
};

const navSub = (label, icon, path) => ({
  label,
  icon,
  command: () => router.push(path),
});

// Items del menú principal
const items = computed(() => {
  const base = [
    navSub('Inicio',     'pi pi-home',        '/'),
    navSub('Agentes',    'pi pi-android',      '/agents'),
    navSub('Orquestador', 'pi pi-microchip-ai', '/orchestrator'),
    navSub('Dashboard',  'pi pi-chart-bar',    '/dashboard'),
    {
      label: 'Financiero',
      icon: 'pi pi-chart-line',
      items: [
        navSub('Subir documento',   'pi pi-upload',   '/financial/wizard'),
        navSub('Centro Documental', 'pi pi-folder',   '/financial/document-center'),
        navSub('Conformidad',       'pi pi-verified', '/financial/statutory-compliance'),
        navSub('Viabilidad',        'pi pi-chart-line', '/financial/business-viability'),
        navSub('Historial análisis', 'pi pi-database', '/financial/analysis-history'),
        navSub('Historial de chat', 'pi pi-history',  '/financial/history'),
      ],
    },
    navSub('Consumo',    'pi pi-bolt',          '/usage'),
    navSub('Integraciones', 'pi pi-link',       '/integrations/events'),
    navSub('Planes',     'pi pi-credit-card',  '/plans'),
    navSub('Mis Pagos',  'pi pi-wallet',       '/payment-history'),
  ];

  // Menú Admin solo visible para admins
  if (authStore.isAdmin) {
    base.push({
      label: 'Admin',
      icon: 'pi pi-cog',
      items: [
        navSub('Gestión de Agentes',    'pi pi-android',       '/admin/agents'),
        navSub('Tenants',               'pi pi-users',         '/tenants'),
        navSub('Credenciales',          'pi pi-key',           '/credentials'),
        navSub('Planes',                'pi pi-id-card',       '/admin/plans'),
        { separator: true },
        navSub('Dashboard de Pagos',    'pi pi-dollar',        '/admin/payments'),
        navSub('Pagos Personalizados',  'pi pi-shopping-cart', '/admin/custom-payments'),
      ],
    });
  }

  return base;
});
</script>

<style scoped>
.navbar {
  margin-bottom: 0;
  border-radius: 0;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.navbar-brand {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #1e293b;
  font-weight: 700;
  font-size: 1.2rem;
  margin-right: 1rem;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.user-name {
  font-size: 0.82rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0 0.5rem;
  border-right: 1px solid #e5e7eb;
  margin-right: 0.25rem;
}
</style>
